/* eslint-disable consistent-return */

import { Mongo } from 'meteor/mongo';
import SimpleSchema from 'simpl-schema';
import { includes, values, map } from 'lodash';

import requiredIf from '../../modules/schema-conditional-required';

import { LAYER_SUBTYPES, GEOJSON_API_BASED_SUBTYPES } from '../Layers/Layers';

/**
 * A style object for ipsilum maps.
 *
 * @typedef {object} Style
 * @property {string} _id - The style id.
 * @property {string} owner - The owner id.
 * @property {string} createdAt - The date when it was created.
 * @property {string} updatedAt - The date when it was last updated.
 * @property {string} deleted - Indicates whether the style is in the trash or not.
 * @property {string} name - The name for the style.
 * @property {string} styleType - The layer subtype the style is made for. One of [GeoJSON,Raster,MapBox,custom].
 * @property {geojsonMapBoxStyle} [geojsonStyle] - The style defined for the geojson (required in [GeoJSON,MapBox]).
 * @property {rasterStyle} [rasterStyle] - The style defined for the raster layer (required in Raster).
 * @property {string} [styleURL] - The url where the style is stored (required in custom).
 * @property {boolean} [isScaledCustom] - Flag to indicate if the custom style is scaled or not (required in custom).
 */

/**
 * The style defined for [GeoJSON,MapBox].
 *
 * @typedef {object} geojsonMapBoxStyle
 * @property {string} type - Simbology type for the style. One of [single,numeric,categorized].
 * @property {string} propName - The name of the GeoJSON property to use in categorized symbology.
 * @property {number} numIntervals - The number of intervals to divide the data.
 * @property {number[]} dataLimits - The limits for the data domain.
 * @property {string} geometry - The geometry of the geojson. One of [Point,LineString,Polygon].
 * @property {styleData} styleData - Object to store the defined style.
 * @property {number} tolerance - The tolerance of the GeoJSON VT layer.
 */

/**
 * The style defined for the raster layer.
 *
 * @typedef {object} rasterStyle
 * @property {number} opacity - The opacity value for the raster layer.
 * @property {string} [sld] - A SLD url.
 */

/**
 * The object that stores the user defined style (needed in [GeoJSON,MapBox]).
 *
 * @typedef {object} styleData
 * @property {string} color - The color for the style.
 * @property {legendData[]} legend - The legend for the numeric and categorized styles.
 * @property {string} colorRange - The name of the color range.
 * @property {number} alpha - The trasparency for the style.
 * @property {number} weight - The weight of the line for the style.
 * @property {number} borderAlpha - The trasparency of the outline for the style.
 * @property {string} borderColor -The color of the outline in hexadecimal.
 * @property {string} iconClass - The classes of the font-awesome icon (needed in [Icon]).
 * @property {string} borderStyle - The dashed style of the border.
 * @property {string} pointType - The type of point to draw (needed in geometry [Point]).
 * @property {number} pointSize - The size or radius of the point to draw (needed in geometry [Point]).
 * @property {string} svgCategoryId - The chosen category of the svg to draw (needed in [Marker]).
 * @property {string} svgUrl - The url of the svg to draw (needed in [Marker]).
 */

/**
 * The legend for the numeric and categorized styles.
 *
 * @typedef {object} legendData
 * @property {string|number|boolean} value - The value of the property in the Feature or the range.
 * @property {string} color - The color for this value or range.
 * @property {number} count - The number of times this value appears in the geojson.
 * @property {number} index - An integer to sort the values.
 */

export const DEFAULT = 'default';

export const STYLE_TYPES = {
  GEOJSON: 'GeoJSON',
  RASTER: 'Raster',
  MAPBOX: 'MapBox',
  CUSTOM: 'custom',
  properties: {
    GEOJSON: {
      name: 'GEOJSON',
      value: 'GeoJSON',
      humanValue: 'GeoJSON'
    },
    RASTER: {
      name: 'RASTER',
      value: 'Raster',
      humanValue: 'Raster'
    },
    MAPBOX: {
      name: 'MAPBOX',
      value: 'MapBox',
      humanValue: 'MapBox'
    },
    CUSTOM: {
      name: 'CUSTOM',
      value: 'custom',
      humanValue: 'Custom'
    }
  }
};

export const PROP_DATA_TYPE = {
  NUMBER: 'number',
  STRING: 'string',
  NIL: 'nil'
};

export const SYMBOLOGY_TYPES = {
  SINGLE: 'single',
  CATEGORIZED: 'categorized',
  NUMERIC: 'numeric',
  properties: {
    SINGLE: {
      name: 'SINGLE',
      value: 'single',
      humanValue: 'Single'
    },
    CATEGORIZED: {
      name: 'CATEGORIZED',
      value: 'categorized',
      humanValue: 'Thematic by fixed value'
    },
    NUMERIC: {
      name: 'NUMERIC',
      value: 'numeric',
      humanValue: 'Thematic by ranges'
    }
  }
};

export const GEOJSON_GEOMETRIES = {
  POINT: 'Point',
  LINESTRING: 'LineString',
  POLYGON: 'Polygon',
  MULTIPOLYGON: 'MultiPolygon'
};

export const BORDER_STYLES_TYPES = {
  SOLID: '',
  DASHED: '20',
  LONG_DASH: '40 20',
  DOTTED: '2 10',
  DOT_DASH: '20 20 2 20',
  properties: {
    SOLID: {
      name: 'SOLID',
      value: '',
      humanValue: 'Solid'
    },
    DASHED: {
      name: 'DASHED',
      value: '20',
      humanValue: 'Dashed'
    },
    LONG_DASH: {
      name: 'LONG_DASH',
      value: '40 20',
      humanValue: 'Long Dash'
    },
    DOTTED: {
      name: 'DOTTED',
      value: '2 10',
      humanValue: 'Dotted'
    },
    DOT_DASH: {
      name: 'DOT_DASH',
      value: '20 20 2 20',
      humanValue: 'Dot Dash'
    }
  }
};

export const POINT_TYPES = {
  ICON: 'icon',
  MARKER: 'marker',
  CIRCLEMARKER: 'circleMarker',
  properties: {
    ICON: {
      name: 'ICON',
      value: 'icon',
      humanValue: 'Icon'
    },
    MARKER: {
      name: 'MARKER',
      value: 'marker',
      humanValue: 'Marker'
    },
    CIRCLEMARKER: {
      name: 'CIRCLEMARKER',
      value: 'circleMarker',
      humanValue: 'Circle Marker'
    }
  }
};

export const getStyleType = layerSubType => {
  if (
    includes(
      [
        LAYER_SUBTYPES.GEOIMAGE,
        LAYER_SUBTYPES.TMS,
        LAYER_SUBTYPES.TILE_SERVER,
        LAYER_SUBTYPES.WMS,
        LAYER_SUBTYPES.WMTS,
        LAYER_SUBTYPES.MAPBOXGL
      ],
      layerSubType
    )
  ) {
    return STYLE_TYPES.RASTER;
  }
  if (
    includes(
      [
        ...GEOJSON_API_BASED_SUBTYPES,
        LAYER_SUBTYPES.ANNOTATIONS,
        LAYER_SUBTYPES.GEOJSONVT,
        LAYER_SUBTYPES.INDOOR,
        LAYER_SUBTYPES.REALTIME,
        LAYER_SUBTYPES.ANNOTATIONSREALTIME,
        LAYER_SUBTYPES.TOPOJSON
      ],
      layerSubType
    )
  ) {
    return STYLE_TYPES.GEOJSON;
  }
  if (LAYER_SUBTYPES.MAPBOX === layerSubType) {
    return STYLE_TYPES.MAPBOX;
  }
  return '';
};

const Styles = new Mongo.Collection('Styles');

Styles.allow({
  insert: () => false,
  update: () => false,
  remove: () => false
});

Styles.deny({
  insert: () => true,
  update: () => true,
  remove: () => true
});

const legendSchema = new SimpleSchema({
  value: {
    type: SimpleSchema.oneOf(String, Number, Boolean),
    label: 'The value of the property in the Feature'
  },
  color: {
    type: String,
    label: 'The color for this value'
  },
  count: {
    type: Number,
    label: 'The number of times this value appears in the geojson'
  },
  index: {
    type: Number,
    label: 'An integer to sort the values'
  }
});

const styleDataSchema = new SimpleSchema({
  color: {
    type: String,
    label: 'The color in hexadecimal',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: this.field('type').value === SYMBOLOGY_TYPES.SINGLE,
        schemaInstance: this
      });
    }
  },
  legend: {
    type: Array,
    label: 'The legend for the numeric and categorized styles',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: includes(
          [SYMBOLOGY_TYPES.CATEGORIZED, SYMBOLOGY_TYPES.NUMERIC],
          this.field('type').value
        ),
        schemaInstance: this
      });
    }
  },
  'legend.$': {
    type: legendSchema,
    label: 'The legend for each value in the style',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: includes(
          [SYMBOLOGY_TYPES.CATEGORIZED, SYMBOLOGY_TYPES.NUMERIC],
          this.field('type').value
        ),
        schemaInstance: this
      });
    }
  },
  colorRange: {
    type: String,
    label: 'The name of the color range',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: includes(
          [SYMBOLOGY_TYPES.CATEGORIZED, SYMBOLOGY_TYPES.NUMERIC],
          this.field('type').value
        ),
        schemaInstance: this
      });
    }
  },
  alpha: {
    type: Number,
    label: 'The trasparency for the style',
    min: 0,
    max: 1
  },
  weight: {
    type: Number,
    label: 'The weight of the line for the style',
    min: 0
  },
  borderAlpha: {
    type: Number,
    label: 'The trasparency of the outline for the style',
    min: 0,
    max: 1,
    optional: true
  },
  borderColor: {
    type: String,
    label: 'The color of the outline in hexadecimal',
    optional: true
  },
  iconClass: {
    type: String,
    label: 'The classes of the font-awesome icon',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: this.field('pointType').value === POINT_TYPES.ICON,
        schemaInstance: this
      });
    }
  },
  borderStyle: {
    type: String,
    label: 'The dashed style of the border',
    optional: true,
    allowedValues: map(BORDER_STYLES_TYPES.properties, 'value')
  },
  pointType: {
    type: String,
    label: 'The type of point to draw',
    optional: true,
    allowedValues: map(POINT_TYPES.properties, 'value')
  },
  pointSize: {
    type: Number,
    label: 'The size or radius of the point to draw',
    optional: true,
    min: 0
  },
  svgCategoryId: {
    type: String,
    label: 'The chosen category of the svg to draw',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: this.field('pointType').value === POINT_TYPES.MARKER,
        schemaInstance: this
      });
    }
  },
  svgUrl: {
    type: String,
    label: 'The url of the svg to draw.',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: this.field('pointType').value === POINT_TYPES.MARKER,
        schemaInstance: this
      });
    }
  },
  isScaledSymbology: {
    type: Boolean,
    label: 'Flag that checks if the symology is scaled or not',
    optional: true
  }
});

const geojsonMapBoxStyleDataSchema = new SimpleSchema({
  type: {
    type: String,
    label: 'The simbology type for the style',
    allowedValues: map(SYMBOLOGY_TYPES.properties, 'value')
  },
  propName: {
    type: String,
    label: 'The name of the GeoJSON property to use in categorized symbology',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: includes(
          [SYMBOLOGY_TYPES.CATEGORIZED, SYMBOLOGY_TYPES.NUMERIC],
          this.field('type').value
        ),
        schemaInstance: this
      });
    }
  },
  numIntervals: {
    type: Number,
    label: 'The number of intervals to divide the data',
    min: 1,
    max: 500,
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: SYMBOLOGY_TYPES.NUMERIC === this.field('type').value,
        schemaInstance: this
      });
    }
  },
  dataLimits: {
    type: Array,
    label: 'The limits for the data domain',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: SYMBOLOGY_TYPES.NUMERIC === this.field('type').value,
        schemaInstance: this
      });
    }
  },
  'dataLimits.$': {
    type: Number,
    label: 'A limit in the data domain',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: SYMBOLOGY_TYPES.NUMERIC === this.field('type').value,
        schemaInstance: this
      });
    }
  },
  geometry: {
    type: String,
    label: 'The geometry type of the geojson',
    allowedValues: values(GEOJSON_GEOMETRIES),
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: this.field('styleType').value === STYLE_TYPES.GEOJSON,
        schemaInstance: this
      });
    }
  },
  styleData: {
    type: styleDataSchema,
    label: 'The object that stores the user defined style'
  },
  tolerance: {
    type: Number,
    label: 'The tolerance for the GeoJSON VT layer',
    optional: true
  }
});

const rasterStyleDataSchema = new SimpleSchema({
  opacity: {
    type: Number,
    label: 'The opacity value for the raster layer'
  },
  sld: {
    type: String,
    optional: true,
    label: 'A SLD url'
  }
});

Styles.schema = new SimpleSchema({
  owner: {
    type: String,
    label: 'The ID of the user this style belongs to.'
  },
  createdAt: {
    type: String,
    label: 'The date this style was created in the database.',
    autoValue() {
      if (this.isInsert) return new Date().toISOString();
    }
  },
  updatedAt: {
    type: String,
    label: 'The date this style was last updated.',
    autoValue() {
      if (this.isInsert || this.isUpdate) return new Date().toISOString();
    }
  },
  name: {
    type: String,
    label: 'A name to identify the style.'
  },
  styleType: {
    type: String,
    label: 'The type of the style added',
    allowedValues: map(STYLE_TYPES.properties, 'value')
  },
  geojsonStyle: {
    type: geojsonMapBoxStyleDataSchema,
    optional: true,
    label: 'The style choosen by the user',
    custom() {
      return requiredIf({
        requiredCondition: includes(
          [STYLE_TYPES.MAPBOX, STYLE_TYPES.GEOJSON],
          this.field('styleType').value
        ),
        schemaInstance: this
      });
    }
  },
  rasterStyle: {
    type: rasterStyleDataSchema,
    optional: true,
    label: 'The style choosen by the user',
    custom() {
      return requiredIf({
        requiredCondition: this.field('styleType').value === STYLE_TYPES.RASTER,
        schemaInstance: this
      });
    }
  },
  styleURL: {
    type: String,
    optional: true,
    label: 'The url to the style choosen by the user',
    custom() {
      return requiredIf({
        requiredCondition: this.field('styleType').value === STYLE_TYPES.CUSTOM,
        schemaInstance: this
      });
    }
  },
  isScaledCustom: {
    type: Boolean,
    optional: true,
    label: 'Flag to indicate if the custom style is scaled or not',
    custom() {
      return requiredIf({
        requiredCondition: this.field('styleType').value === STYLE_TYPES.CUSTOM,
        schemaInstance: this
      });
    }
  },
  deleted: {
    type: String,
    label: 'The date the style was deleted.',
    autoValue() {
      if (this.isInsert) return 'no';
    }
  }
});

Styles.attachSchema(Styles.schema);

export default Styles;
